import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import GroupsPickerDropdownPanel from './GroupsPickerDropdownPanel';
import { computeFloatingPanelBelow } from './computeFloatingPanelBelow';

const Z_INDEX = 99999;

/**
 * Assign To / Assigned Roles picker — portaled to document.body (create & edit modes).
 */
const GroupsPickerDropdownPortal = forwardRef(function GroupsPickerDropdownPortal(
  {
    isOpen,
    anchorRef,
    title,
    subtitle,
    count = 0,
    minWidth = 256,
    children,
  },
  ref
) {
  const internalPanelRef = useRef(null);
  const panelRef = ref || internalPanelRef;
  const [position, setPosition] = useState(null);

  const syncPosition = useCallback(() => {
    return computeFloatingPanelBelow(anchorRef?.current, { minWidth });
  }, [anchorRef, minWidth]);

  useLayoutEffect(() => {
    if (!isOpen || !panelRef.current || !anchorRef?.current) return;
    const next = computeFloatingPanelBelow(anchorRef.current, { minWidth });
    if (next) setPosition(next);
  }, [isOpen, minWidth, anchorRef, children, count]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return undefined;
    }

    const update = () => {
      const next = syncPosition();
      if (next) setPosition(next);
    };

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, syncPosition]);

  if (!isOpen || !position || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <GroupsPickerDropdownPanel
      ref={panelRef}
      variant="portal"
      title={title}
      subtitle={subtitle}
      count={count}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: position.width,
        zIndex: Z_INDEX,
      }}
    >
      {children}
    </GroupsPickerDropdownPanel>,
    document.body
  );
});

export default GroupsPickerDropdownPortal;
